import {
  Paper,
  Skeleton,
  Stack,
  TrendingDownIcon,
  TrendingUpIcon,
  Typography,
  ToolTip
} from '@esgian/esgianui';
import { formatNumberReadable, parseNumberToSignificant } from '@helpers';
import PropTypes from 'prop-types';
import React, { useMemo } from 'react';
import { propTypes } from 'react-wavify';

function KpiChangeDisplay({ value, percentChange, title, invert, unit, loading, tooltipText, tooltipEnterDelayMs, tooltipPlacement }) {
  const getColor = (value) => {
    if (Math.abs(percentChange) === 0.0) return 'success';
    if (value >= 0) {
      if (invert) {
        return 'success';
      }
      return 'error';
    } else {
      if (invert) {
        return 'error';
      }
      return 'success';
    }
  };

  const getArrow = () => {
    if (percentChange === 'N/A' || Math.abs(percentChange) === 0.0) {
      return <></>;
    }
    if (value >= 0) {
      return <TrendingUpIcon color={getColor(value)} fontSize={'small'} />;
    }
    return <TrendingDownIcon color={getColor(value)} fontSize={'small'} />;
  };

  if (loading) {
    return (
      <Paper sx={{ height: '80px', width: 'max-content' }}>
        <Stack alignItems={'center'}>
          <Stack direction={'row'} spacing={0.5} sx={{ height: '40px' }}>
            <Skeleton variant={'rounded'} height={'38px'} width={'75px'} />
            <Skeleton
              sx={{ alignSelf: 'flex-end' }}
              variant={'rounded'}
              height={'1.25em'}
              width={'1.7em'}
            />
          </Stack>
          {typeof title === 'string' ? (
            <Typography variant={'caption'} color={'text.disabled'}>
              {title}
            </Typography>
          ) : (
            title
          )}
        </Stack>
      </Paper>
    );
  }

  const isNoData = value == null || Number.isNaN(value);

  const valueSign = useMemo(() => {
    if (isNoData || Math.abs(percentChange) === 0.0) {
      return '';
    }

    return value >= 0 ? `+` : '-';
  }, [value, isNoData]);

  return (
    <Paper sx={{ height: '80px', width: '320px' }}>
      <Stack alignItems={'center'}>
        <ToolTip 
          title={tooltipText} 
          enterDelay={tooltipEnterDelayMs} 
          enterNextDelay={500} 
          placement={tooltipPlacement}>
        <Stack direction={'row'} spacing={1}>
          <Stack direction={'row'} alignItems="baseline" spacing={0.1}>
            <Typography variant={'h4'}>{isNoData ? '-' : `${valueSign}${formatNumberReadable(
              parseNumberToSignificant(Math.abs(value))
            )}`}</Typography>
            <Typography variant={'h6'}>{isNoData ? '' : unit}</Typography>
          </Stack>
          <Stack direction={'row'} sx={{ alignSelf: 'self-end', pb: 0.5 }}>
            <Typography
              variant={'body2'}
              sx={{
                color: ({ palette }) => {
                  if (isNoData || percentChange === 'N/A') {
                    return palette.text.disabled;
                  }
                  return palette[getColor(value)].main;
                }
              }}>
              {!isNoData && percentChange !== 'N/A' && percentChange !== 'NaN'
                ? `${Math.abs(percentChange)}%`
                : 'N/A'}
            </Typography>
            {!isNoData && getArrow()}
          </Stack>
        </Stack>
        </ToolTip>
        {typeof title === 'string' ? (
          <Typography variant={'caption'} color={'text.disabled'}>
            {title}
          </Typography>
        ) : (
          title
        )}
      </Stack>
    </Paper>
  );
}

KpiChangeDisplay.propTypes = {
  value: PropTypes.number,
  percentChange: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  invert: PropTypes.bool,
  loading: PropTypes.bool,
  unit: PropTypes.string,
  useTooltip: PropTypes.bool,
  tooltipText: PropTypes.string,
  tooltipEnterDelayMs: PropTypes.number,
  tooltipPlacement: PropTypes.oneOf([
    'bottom-end',
    'bottom-start',
    'bottom',
    'left-end',
    'left-start',
    'left',
    'right-end',
    'right-start',
    'right',
    'top-end',
    'top-start',
    'top'
])
};

KpiChangeDisplay.defaultProps = {
  value: 0,
  percentChange: 0,
  title: '',
  invert: false,
  loading: false,
  unit: '',
  useTooltip: false,
  tooltipEnterDelayMs: 100,
  tooltipPlacement: 'top'
};

export default KpiChangeDisplay;
